import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Send, Sparkles, BookOpen, Scale, FileText, Clock,
  ThumbsUp, ThumbsDown, Copy, Share2, Bookmark,
  Mic, Paperclip, History, Star, ArrowRight
} from 'lucide-react';

const suggestedQueries = [
  'Latest Supreme Court judgments on Article 21',
  'Landmark cases on Right to Privacy',
  'Section 138 NI Act - Recent amendments',
  'Bail provisions under BNSS 2023',
  'Consumer Protection Act 2019 - Key changes',
];

const recentSearches = [
  { id: 1, query: 'Specific relief act section 12', time: '2 hours ago' },
  { id: 2, query: 'Motor accident claims tribunal procedure', time: '5 hours ago' },
  { id: 3, query: 'POCSO Act - Evidence requirements', time: 'Yesterday' },
];

const Research = () => {
  const { isDark } = useTheme();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);

  const handleSearch = () => {
    if (!query.trim()) return;
    
    setIsSearching(true);
    setMessages(prev => [...prev, { type: 'user', content: query }]);
    setQuery('');

    // Simulate AI response
    setTimeout(() => {
      setMessages(prev => [...prev, {
        type: 'ai',
        content: `Based on my analysis of Indian legal databases, here's what I found regarding "${query}":\n\n**Key Findings:**\n\n1. **Supreme Court Precedents:** Multiple landmark judgments have established clear principles in this area...\n\n2. **Relevant Statutes:** The applicable legal provisions include...\n\n3. **Recent Developments:** As of 2024, there have been significant amendments...\n\n**Citations:**\n- AIR 2024 SC 1234\n- (2023) 5 SCC 567\n- 2024 SCC OnLine Del 890`,
        citations: 12,
        confidence: 94
      }]);
      setIsSearching(false);
    }, 2000);
  };

  return (
    <MainLayout title="AI Legal Research" subtitle="Powered by multi-model AI consensus">
      <div className="grid grid-cols-4 gap-6 h-[calc(100vh-180px)]">
        {/* Sidebar - Recent & Saved */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="glass-card p-4 overflow-y-auto"
        >
          {/* Recent Searches */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5 text-blue-500" />
              <h3 className="font-semibold">Recent Searches</h3>
            </div>
            <div className="space-y-2">
              {recentSearches.map((search) => (
                <motion.button
                  key={search.id}
                  className="w-full p-3 rounded-xl text-left hover:bg-gray-500/10 transition-all"
                  whileHover={{ x: 4 }}
                  onClick={() => setQuery(search.query)}
                >
                  <p className="text-sm line-clamp-1">{search.query}</p>
                  <p className="text-xs text-gray-500 mt-1">{search.time}</p>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Saved Research */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Bookmark className="w-5 h-5 text-yellow-500" />
              <h3 className="font-semibold">Saved Research</h3>
            </div>
            <p className="text-sm text-gray-500 text-center py-4">No saved research yet</p>
          </div>
        </motion.div>

        {/* Main Chat Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="col-span-3 flex flex-col"
        >
          {/* Messages Area */}
          <div className="flex-1 glass-card p-6 mb-4 overflow-y-auto">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center">
                {/* Welcome Screen */}
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center max-w-lg"
                >
                  <motion.div
                    className="w-24 h-24 mx-auto mb-6 rounded-3xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #1E3A5F, #2D8CFF)' }}
                    animate={{ 
                      boxShadow: ['0 0 30px rgba(45,140,255,0.3)', '0 0 50px rgba(45,140,255,0.5)', '0 0 30px rgba(45,140,255,0.3)']
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Sparkles className="w-12 h-12 text-white" />
                  </motion.div>
                  <h2 className="text-2xl font-bold mb-3">AI Legal Research Assistant</h2>
                  <p className="text-gray-500 mb-8">
                    Ask any legal question and get comprehensive research with citations from Indian courts and statutes.
                  </p>

                  {/* Suggested Queries */}
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">Try asking:</p>
                    {suggestedQueries.map((q, index) => (
                      <motion.button
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => setQuery(q)}
                        className="w-full p-4 rounded-xl text-left transition-all flex items-center gap-3 group"
                        style={{
                          background: isDark ? 'rgba(45, 140, 255, 0.1)' : 'rgba(45, 140, 255, 0.05)',
                          border: '1px solid rgba(45, 140, 255, 0.2)'
                        }}
                        whileHover={{ scale: 1.02, x: 5 }}
                      >
                        <BookOpen className="w-5 h-5 text-blue-500" />
                        <span className="flex-1">{q}</span>
                        <ArrowRight className="w-4 h-4 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </div>
            ) : (
              <div className="space-y-6">
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.type === 'user' ? (
                      <div className="max-w-2xl p-4 rounded-2xl bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white">
                        {message.content}
                      </div>
                    ) : (
                      <div className="max-w-3xl">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500">
                            <Sparkles className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="glass-card p-6">
                              <div className="whitespace-pre-wrap">{message.content}</div>
                              
                              {/* Confidence & Citations */}
                              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-500/20">
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-2 rounded-full bg-gray-500/20 overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                                      style={{ width: `${message.confidence}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-green-500">{message.confidence}% confidence</span>
                                </div>
                                <span className="text-sm text-gray-500">{message.citations} citations found</span>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2 mt-3">
                              {[ThumbsUp, ThumbsDown, Copy, Share2, Bookmark].map((Icon, i) => (
                                <motion.button
                                  key={i}
                                  className="p-2 rounded-lg hover:bg-gray-500/10"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <Icon className="w-4 h-4" />
                                </motion.button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}

                {/* Loading Indicator */}
                {isSearching && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500">
                      <Sparkles className="w-5 h-5 text-white animate-pulse" />
                    </div>
                    <div className="glass-card p-6">
                      <div className="flex items-center gap-3">
                        <motion.div
                          className="w-2 h-2 bg-blue-500 rounded-full"
                          animate={{ scale: [1, 1.5, 1] }}
                          transition={{ duration: 0.5, repeat: Infinity }}
                        />
                        <motion.div
                          className="w-2 h-2 bg-blue-500 rounded-full"
                          animate={{ scale: [1, 1.5, 1] }}
                          transition={{ duration: 0.5, repeat: Infinity, delay: 0.1 }}
                        />
                        <motion.div
                          className="w-2 h-2 bg-blue-500 rounded-full"
                          animate={{ scale: [1, 1.5, 1] }}
                          transition={{ duration: 0.5, repeat: Infinity, delay: 0.2 }}
                        />
                        <span className="text-gray-500 ml-2">Searching legal databases...</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* Input Area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-4"
          >
            <div className="flex items-center gap-4">
              <motion.button
                className="p-3 rounded-xl hover:bg-gray-500/10"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Paperclip className="w-5 h-5" />
              </motion.button>
              
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Ask any legal question..."
                className="flex-1 bg-transparent outline-none text-lg"
              />

              <motion.button
                onClick={() => setIsRecording(!isRecording)}
                className={`p-3 rounded-xl ${isRecording ? 'bg-red-500 text-white' : 'hover:bg-gray-500/10'}`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                animate={isRecording ? { scale: [1, 1.1, 1] } : {}}
                transition={isRecording ? { duration: 0.5, repeat: Infinity } : {}}
              >
                <Mic className="w-5 h-5" />
              </motion.button>

              <motion.button
                onClick={handleSearch}
                disabled={!query.trim() || isSearching}
                className="p-4 rounded-xl bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white disabled:opacity-50"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Send className="w-5 h-5" />
              </motion.button>
            </div>

            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-500/20">
              <span className="text-xs text-gray-500">AI Models:</span>
              {['GPT-4', 'Claude', 'Gemini'].map((model) => (
                <span key={model} className="px-2 py-1 text-xs rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-500">
                  {model}
                </span>
              ))}
              <span className="text-xs text-green-500 ml-auto">Multi-model consensus enabled</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default Research;
